import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Column,
  EmptyState,
  Icon,
  PageHeader,
  Select,
  Skeleton,
  Table,
  TextField,
  useConfirm,
  useToast,
} from "stories/Admin";
import { useAuth } from "AuthProvider";
import useFetch from "hooks/useFetch";
import SectionTabs from "pages/AdminLayout/SectionTabs";
import PageModal from "pages/SuperAdminPages/LocationsPage/PageModal";
import {
  deviceDescription,
  DISPLAY_MODES,
  DisplayMode,
  formatAgo,
  formatTime,
  isLive,
  isOnNow,
  modeLabel,
  nowShowing,
  statusBadge,
  TvDevice,
} from "./tvDevices";
import TvPreview from "./TvPreview";
import styles from "./TvPage.module.scss";

/*
 * TV-skjermer: admin lager en 4-sifret kode som tastes inn på TV-en. Da får TV-en tilgang til
 * moskeens innhold; uten kode viser TV-en bare den offentlige infoen om moskeen.
 */

const TvPage: React.FC = () => {
  const { selectedOrgNumber, selectedOrgName, mosqueName } = useAuth();
  const { execute } = useFetch<any>();
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const confirm = useConfirm();
  const toast = useToast();

  const [devices, setDevices] = useState<TvDevice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shown, setShown] = useState<TvDevice | null>(null);

  const load = useCallback(
    async (quiet = false) => {
      if (!selectedOrgNumber) return;
      if (!quiet) setIsLoading(true);
      const result = await executeRef.current(
        `/api/v1/mosques/${selectedOrgNumber}/tv-devices`,
        "GET",
      );
      const list: TvDevice[] = Array.isArray(result) ? result : [];
      setDevices(list);
      // Når TV-en har tastet inn koden, oppdateres dialogen til «tilkoblet».
      setShown((s) => (s ? (list.find((d) => d.id === s.id) ?? s) : s));
      setIsLoading(false);
    },
    [selectedOrgNumber],
  );

  useEffect(() => {
    load();
  }, [load]);

  // TV-ene sier hvert 15. sekund hva de viser; oppdater «Skjermene som er på» like ofte.
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (!document.hidden) load(true);
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [load]);

  const fetchPreview = useCallback(
    (orgNr: string, id: number) =>
      executeRef.current(
        `/api/v1/mosques/${orgNr}/tv-devices/${id}/preview`,
        "GET",
      ),
    [],
  );

  const setDisplayMode = async (device: TvDevice, mode: DisplayMode | "") => {
    const result = await executeRef.current(
      `/api/v1/mosques/${selectedOrgNumber}/tv-devices/${device.id}/display-mode`,
      "PUT",
      { displayMode: mode || null },
    );
    if (result?.id) {
      setDevices((list) => list.map((d) => (d.id === result.id ? result : d)));
      toast.success(
        mode
          ? `«${device.name}» bytter til ${modeLabel(mode)} om noen sekunder.`
          : `«${device.name}» velger utseende selv.`,
      );
    } else {
      toast.error("Kunne ikke lagre utseendet. Prøv igjen.");
    }
  };

  const modePicker = (d: TvDevice) => (
    <Select
      aria-label={`Utseende på «${d.name}»`}
      value={d.displayMode ?? ""}
      onChange={(e) => setDisplayMode(d, e.target.value as DisplayMode | "")}
      options={[{ value: "", label: "TV-en velger selv" }, ...DISPLAY_MODES]}
    />
  );

  // Mens koden vises, sjekk hvert 5. sekund om TV-en har tatt den i bruk.
  const waitingId = shown?.status === "pending" ? shown.id : null;
  useEffect(() => {
    if (!waitingId) return;
    const timer = window.setInterval(load, 5000);
    return () => window.clearInterval(timer);
  }, [waitingId, load]);

  const create = async () => {
    setCreating(true);
    setError(null);
    const result = await executeRef.current(
      `/api/v1/mosques/${selectedOrgNumber}/tv-devices`,
      "POST",
      { name: name.trim() || "TV" },
    );
    setCreating(false);
    if (result?.code) {
      setShown(result);
      setName("");
      load();
    } else {
      setError("Kunne ikke lage koden. Prøv igjen.");
    }
  };

  const newCode = async (device: TvDevice) => {
    if (device.status === "active") {
      const ok = await confirm({
        title: `Koble til «${device.name}» på nytt?`,
        message:
          "Du får en ny kode. Når den tastes inn, slutter TV-en som er koblet til nå å ha tilgang.",
        confirmLabel: "Lag ny kode",
      });
      if (!ok) return;
    }
    const result = await executeRef.current(
      `/api/v1/mosques/${selectedOrgNumber}/tv-devices/${device.id}/code`,
      "POST",
    );
    if (result?.code) {
      setShown(result);
      load();
    }
  };

  const revoke = async (device: TvDevice) => {
    const ok = await confirm({
      title: `Koble fra «${device.name}»?`,
      message:
        "TV-en mister tilgangen med en gang og viser bare den offentlige infoen om moskeen. Dette kan ikke angres, men du kan legge til TV-en på nytt.",
      confirmLabel: "Koble fra",
      tone: "danger",
    });
    if (!ok) return;
    await executeRef.current(
      `/api/v1/mosques/${selectedOrgNumber}/tv-devices/${device.id}`,
      "DELETE",
    );
    setShown(null);
    load();
  };

  const shownMosque = selectedOrgName || mosqueName || "moskeen";
  const onNow = devices.filter((d) => isOnNow(d));

  const columns: Array<Column<TvDevice>> = [
    {
      key: "name",
      header: "TV",
      render: (d) => (
        <span className={styles.stack}>
          <b>{d.name}</b>
          {deviceDescription(d) && (
            <span className={styles.muted}>{deviceDescription(d)}</span>
          )}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (d) => {
        const badge = statusBadge(d);
        return (
          <span className={styles.inline}>
            <Badge tone={badge.tone}>{badge.label}</Badge>
            {d.code && <code className={styles.inlineCode}>{d.code}</code>}
          </span>
        );
      },
    },
    {
      key: "mode",
      header: "Utseende",
      render: (d) => (d.status === "active" ? modePicker(d) : null),
    },
    {
      key: "lastSeen",
      header: "Sist aktiv",
      numeric: true,
      render: (d) => formatAgo(d.lastSeenAt),
    },
    {
      key: "created",
      header: "Lagt til",
      render: (d) => (
        <span className={styles.stack}>
          <span>{formatAgo(d.createdAt)}</span>
          <span className={styles.muted}>{d.createdByEmail || ""}</span>
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (d) =>
        d.status !== "revoked" && (
          <span className={styles.actions}>
            <Button
              size="sm"
              variant="secondary"
              onClick={() =>
                d.status === "pending" ? setShown(d) : newCode(d)
              }
            >
              {d.status === "pending" ? "Vis kode" : "Ny kode"}
            </Button>
            <Button size="sm" variant="danger" onClick={() => revoke(d)}>
              Koble fra
            </Button>
          </span>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="TV-skjermer"
        subtitle={`Koble TV-ene i ${shownMosque} til iBønn med en kode. Uten kode viser TV-en bare den offentlige infoen om moskeen.`}
      />
      <SectionTabs />

      <Card title="Slik kobler dere til en TV">
        <ol className={styles.steps}>
          <li>
            Gi TV-en et navn, f.eks. «Hovedsalen» eller «Kvinneavdelingen», og
            trykk «Lag kode».
          </li>
          <li>
            Åpne iBønn-appen på TV-en og tast inn den 4-sifrede koden. Koden
            virker i en time og kan bare brukes én gang.
          </li>
          <li>
            Her ser dere når hver TV sist var aktiv. Kobler dere fra en TV,
            mister den tilgangen med en gang.
          </li>
        </ol>
        <div className={styles.createRow}>
          <TextField
            label="Navn på TV-en"
            placeholder="F.eks. Hovedsalen"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            fieldClassName={styles.createName}
          />
          <Button
            onClick={create}
            loading={creating}
            disabled={!selectedOrgNumber}
            icon={<Icon name="tv" size={16} strokeWidth={2} />}
          >
            Lag kode
          </Button>
        </div>
        {error && <Alert tone="error">{error}</Alert>}
      </Card>

      {onNow.length > 0 && (
        <Card
          title="Skjermene som er på"
          subtitle="Slik ser TV-ene ut akkurat nå. Velg utseende for hver TV, så bytter den av seg selv i løpet av noen sekunder."
        >
          <div className={styles.screens}>
            {onNow.map((d) => {
              const live = isLive(d);
              const showing = nowShowing(d);
              const pending =
                live &&
                !!d.displayMode &&
                (d.shownScreen !== "display" || d.shownMode !== d.displayMode);
              return (
                <div key={d.id} className={styles.screenCard}>
                  {live ? (
                    <TvPreview
                      orgNr={d.orgNr}
                      deviceId={d.id}
                      name={d.name}
                      mode={(d.shownMode as DisplayMode) || "STANDARD"}
                      screen={d.shownScreen === "modes" ? "modes" : "display"}
                      fetchPreview={fetchPreview}
                    />
                  ) : (
                    <div className={styles.screen}>
                      <div className={styles.screenOverlay}>
                        {d.platform === "web-tv"
                          ? "Last inn ibønn.no/tv på nytt på TV-en for å se hva den viser her."
                          : "Denne TV-appen sier ikke hva den viser ennå. Utseendet kan velges likevel."}
                      </div>
                    </div>
                  )}
                  <div className={styles.screenInfo}>
                    <span className={styles.stack}>
                      <b>{d.name}</b>
                      <span className={styles.muted}>
                        {pending
                          ? `Bytter til ${modeLabel(d.displayMode)} …`
                          : showing ||
                            `Sist aktiv ${formatAgo(d.lastSeenAt).toLowerCase()}`}
                      </span>
                    </span>
                    {modePicker(d)}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Card title="TV-er">
        {isLoading && devices.length === 0 ? (
          <div className={styles.skeletons}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} height={36} />
            ))}
          </div>
        ) : (
          <Table
            columns={columns}
            rows={devices}
            rowKey={(d) => d.id}
            rowState={(d) => (d.status === "revoked" ? "dim" : undefined)}
            caption="TV-skjermer"
            empty={
              <EmptyState icon="tv" title="Ingen TV-er ennå">
                Lag en kode over for å koble til den første TV-en.
              </EmptyState>
            }
          />
        )}
      </Card>

      <PageModal
        open={!!shown}
        title={shown?.name || "TV"}
        icon="tv"
        onClose={() => setShown(null)}
        footer={
          <Button onClick={() => setShown(null)}>
            {shown?.status === "active" ? "Ferdig" : "Lukk"}
          </Button>
        }
      >
        {shown?.status === "active" ? (
          <Alert tone="ok">
            TV-en er koblet til. Den viser nå innholdet fra {shownMosque}.
          </Alert>
        ) : shown?.code ? (
          <>
            <span className={styles.muted}>
              Tast inn denne koden i iBønn-appen på TV-en:
            </span>
            <div className={styles.bigCode} aria-label={`Kode ${shown.code}`}>
              {shown.code.split("").map((digit, i) => (
                <span key={i}>{digit}</span>
              ))}
            </div>
            <span className={styles.muted}>
              Gyldig til kl. {formatTime(shown.codeExpiresAt)}. Denne siden
              oppdateres når TV-en er koblet til.
            </span>
          </>
        ) : (
          <Alert tone="warn">
            Koden har gått ut. Lukk og trykk «Ny kode» for å lage en ny.
          </Alert>
        )}
      </PageModal>
    </>
  );
};

export default TvPage;
